import { matchRoutes } from "react-router-dom";
import type { PlayerRouteMetadata } from "./routeMetadata.types";

function route(
  path: string,
  primaryJourney: PlayerRouteMetadata["primaryJourney"],
  access: PlayerRouteMetadata["access"],
  title: string,
  backTo: string | null,
  queryState: PlayerRouteMetadata["queryState"] = "none",
  shell: PlayerRouteMetadata["shell"] = "app",
): PlayerRouteMetadata {
  return { path, primaryJourney, access, title, backTo, queryState, shell };
}

/** Current registered routes. Child cutovers change their pages and this inventory together. */
export const PLAYER_ROUTE_METADATA: readonly PlayerRouteMetadata[] = [
  route("/login", null, "public", "Sign in", "/", "auth", "standalone"),
  route(
    "/register",
    null,
    "public",
    "Create account",
    "/login",
    "auth",
    "standalone",
  ),
  route(
    "/forgot-password",
    null,
    "public",
    "Reset password",
    "/login",
    "auth",
    "standalone",
  ),
  route(
    "/reset-password",
    null,
    "public",
    "Set password",
    "/login",
    "auth",
    "standalone",
  ),
  route(
    "/verify-email",
    null,
    "public",
    "Verify email",
    "/login",
    "auth",
    "standalone",
  ),
  route(
    "/terms",
    null,
    "public",
    "Terms of service",
    "/",
    "none",
    "standalone",
  ),
  route(
    "/messaging-program",
    null,
    "public",
    "Transactional messaging program",
    "/",
    "none",
    "standalone",
  ),
  route(
    "/sms-terms",
    null,
    "public",
    "Transactional SMS terms",
    "/",
    "none",
    "standalone",
  ),
  route(
    "/privacy",
    null,
    "public",
    "Privacy policy",
    "/",
    "none",
    "standalone",
  ),
  route(
    "/guardian-consent",
    null,
    "capability",
    "Guardian consent",
    null,
    "auth",
    "standalone",
  ),
  route(
    "/tournament-display",
    null,
    "capability",
    "Tournament display",
    null,
    "auth",
    "standalone",
  ),
  route(
    "/tournament-display/:tournamentId",
    null,
    "capability",
    "Tournament display",
    null,
    "none",
    "standalone",
  ),
  route(
    "/kiosk",
    null,
    "capability",
    "Check-in kiosk",
    null,
    "auth",
    "standalone",
  ),
  route("/", "home", "public", "Home", null),
  route(
    "/facility/:facilityId/coaches",
    "play",
    "public",
    "Coaches",
    "/facility/:facilityId",
    "filters",
  ),
  route(
    "/coaches/:coachProfileId",
    "play",
    "public",
    "Coach",
    "/play",
    "navigation",
  ),
  route(
    "/facility/:facilityId",
    "play",
    "public",
    "Facility",
    "/play",
    "navigation",
  ),
  route(
    "/facility/:facilityId/memberships",
    "play",
    "public",
    "Memberships",
    "/facility/:facilityId",
  ),
  route(
    "/classes/:facilityId",
    "play",
    "public",
    "Facility classes",
    "/facility/:facilityId",
    "filters",
  ),
  route(
    "/classes/:facilityId/:programId",
    "play",
    "public",
    "Class",
    "/facility/:facilityId",
    "navigation",
  ),
  route(
    "/compete/tournaments",
    "compete",
    "public",
    "Tournaments",
    "/",
    "filters",
  ),
  route(
    "/tournaments/:tournamentId",
    "compete",
    "public",
    "Tournament",
    "/compete/tournaments",
    "navigation",
  ),
  route(
    "/onboarding",
    null,
    "authenticated",
    "Player setup",
    null,
    "auth",
    "standalone",
  ),
  route(
    "/me/moderation",
    "me",
    "recovery",
    "Account status",
    "/me",
    "none",
    "recovery",
  ),
  route(
    "/me/privacy",
    "me",
    "recovery",
    "Privacy and data",
    "/me",
    "none",
    "recovery",
  ),
  route("/play", "play", "public", "Play", "/", "navigation-and-filters"),
  route(
    "/place/:placeId",
    "play",
    "public",
    "Community place",
    "/play",
    "navigation",
  ),
  route(
    "/facility/:facilityId/memberships/:planId",
    "play",
    "profile",
    "Membership plan",
    "/facility/:facilityId/memberships",
    "navigation",
  ),
  route(
    "/book/:facilityId",
    "play",
    "profile",
    "Book a court",
    "/facility/:facilityId",
    "navigation-and-filters",
  ),
  route(
    "/book/:facilityId/review",
    "play",
    "profile",
    "Review booking",
    "/book/:facilityId",
    "navigation",
  ),
  route(
    "/book/:facilityId/bookings",
    "me",
    "profile",
    "Facility bookings",
    "/me/bookings",
    "filters",
  ),
  route(
    "/book/:facilityId/bookings/:bookingId",
    "me",
    "profile",
    "Booking",
    "/me/bookings",
    "navigation",
  ),
  route(
    "/dropin/:facilityId",
    "play",
    "profile",
    "Join drop-in",
    "/facility/:facilityId",
    "navigation",
  ),
  route(
    "/dropin/:facilityId/waiver",
    "play",
    "profile",
    "Drop-in waiver",
    "/dropin/:facilityId",
    "navigation",
  ),
  route(
    "/dropin/:facilityId/check-in",
    "play",
    "profile",
    "Check in",
    "/dropin/:facilityId",
  ),
  route(
    "/dropin/:facilityId/check-in/:subscriptionId",
    "play",
    "profile",
    "Check-in code",
    "/dropin/:facilityId/check-in",
  ),
  route(
    "/dropin/:facilityId/session",
    "play",
    "profile",
    "Drop-in session",
    "/dropin/:facilityId",
  ),
  route(
    "/dropin/:facilityId/queue/:entryId",
    "play",
    "profile",
    "Court queue",
    "/dropin/:facilityId/session",
  ),
  route(
    "/dropin/:facilityId/court-board",
    "play",
    "profile",
    "Court board",
    "/dropin/:facilityId/session",
  ),
  route("/me/invitations", "me", "profile", "Invitations", "/me", "navigation"),
  route("/dropin/party/:partyId", "play", "profile", "Drop-in party", "/play"),
  route(
    "/classes/:facilityId/:programId/enroll/:trackId",
    "play",
    "profile",
    "Enroll in class",
    "/classes/:facilityId/:programId",
    "navigation",
  ),
  route(
    "/classes/:facilityId/:programId/learning/:enrollmentId",
    "me",
    "profile",
    "Class learning",
    "/me/classes",
    "navigation",
  ),
  route(
    "/me/coaching-experience",
    "me",
    "profile",
    "Coaching experience",
    "/me/account",
  ),
  route(
    "/classes/:programId/rate-coach",
    "me",
    "profile",
    "Rate your coach",
    "/me/classes",
  ),
  route(
    "/compete/mine",
    "compete",
    "profile",
    "My competitions",
    "/compete/tournaments",
    "navigation-and-filters",
  ),
  route(
    "/compete/leagues",
    "compete",
    "public",
    "Leagues",
    "/compete/tournaments",
    "navigation-and-filters",
  ),
  route("/me/performance", "me", "profile", "Performance", "/me"),
  route(
    "/me/achievements",
    "me",
    "profile",
    "Achievements & streaks",
    "/me/performance",
  ),
  route(
    "/me/rewards",
    "me",
    "profile",
    "Rewards & referrals",
    "/me",
  ),
  route(
    "/me/performance/matches",
    "me",
    "profile",
    "Match history",
    "/me/performance",
    "filters",
  ),
  route(
    "/me/performance/matches/:matchId",
    "me",
    "profile",
    "Match detail",
    "/me/performance/matches",
  ),
  route(
    "/rankings",
    "compete",
    "profile",
    "Rankings",
    "/compete/tournaments",
    "filters",
  ),
  route("/players/:userId", null, "profile", "Player profile", null),
  route(
    "/community",
    "social",
    "profile",
    "Community",
    "/",
    "navigation-and-filters",
  ),
  route(
    "/community/feed",
    "home",
    "profile",
    "Home feed",
    null,
  ),
  route(
    "/me/friends",
    "me",
    "profile",
    "Friends",
    "/me/account",
    "navigation-and-filters",
  ),
  route(
    "/community/friends",
    "me",
    "profile",
    "Friends",
    "/me/account",
    "navigation-and-filters",
  ),
  route(
    "/me/safety/blocked",
    "me",
    "profile",
    "Blocked players",
    "/me/account",
  ),
  route(
    "/community/messages",
    null,
    "profile",
    "Messages",
    null,
    "navigation",
    "messaging",
  ),
  route(
    "/community/messages/:conversationId",
    null,
    "profile",
    "Conversation",
    "/community/messages",
    "navigation",
    "messaging",
  ),
  route(
    "/community/inquiries",
    null,
    "profile",
    "Service inquiries",
    "/community/messages",
    "navigation",
  ),
  route(
    "/community/inquiries/:conversationId",
    null,
    "profile",
    "Coaching inquiry",
    "/community/inquiries",
    "navigation",
  ),
  route(
    "/community/communities/:communityId",
    "social",
    "profile",
    "Community",
    "/community",
    "navigation",
  ),
  route(
    "/community/communities/:communityId/channels/:conversationId",
    "social",
    "profile",
    "Community channel",
    "/community/communities/:communityId",
    "navigation",
    "messaging",
  ),
  route(
    "/community/posts/:postId",
    "social",
    "profile",
    "Post",
    "/",
  ),
  route("/me", "me", "profile", "Activity", "/", "navigation"),
  route("/me/account", "me", "profile", "Account", "/me", "navigation"),
  route("/notifications", null, "profile", "Notifications", null, "filters"),
  route("/search", null, "public", "Search", null, "filters"),
  route("/me/edit", "me", "profile", "Edit profile", "/me"),
  route(
    "/me/calendar",
    "me",
    "profile",
    "Calendar",
    "/me",
    "navigation-and-filters",
  ),
  route(
    "/me/bookings",
    "me",
    "profile",
    "Bookings",
    "/me",
    "navigation-and-filters",
  ),
  route("/me/classes", "me", "profile", "My classes", "/me", "filters"),
  route("/me/lessons", "me", "profile", "My lessons", "/me", "filters"),
  route(
    "/me/lessons/:requestId",
    "me",
    "profile",
    "Coaching request",
    "/me/lessons",
    "navigation",
  ),
  route(
    "/compete/leagues/divisions/:divisionId/standings",
    "compete",
    "profile",
    "Division standings",
    "/compete/leagues/divisions/:divisionId",
  ),
  route(
    "/compete/leagues/divisions/:divisionId",
    "compete",
    "public",
    "League division",
    "/compete/leagues",
    "navigation",
  ),
  route(
    "/me/notifications",
    "me",
    "profile",
    "Notification settings",
    "/me/account",
  ),
  route(
    "/me/transactions/:transactionId",
    "me",
    "profile",
    "Transaction confirmation",
    "/me/transactions",
  ),
  route(
    "/me/transactions",
    "me",
    "profile",
    "Payment history",
    "/me/account",
    "filters",
  ),
  route("/me/payments", "me", "profile", "Saved cards", "/me/account"),
  route(
    "/me/preferences",
    "me",
    "profile",
    "Display preferences",
    "/me/account",
  ),
  route("/me/memberships", "me", "profile", "My memberships", "/me", "filters"),
  route(
    "/me/memberships/:subscriptionId",
    "me",
    "profile",
    "Membership",
    "/me/memberships",
  ),
  route(
    "/me/family/approvals",
    "me",
    "profile",
    "Family approvals",
    "/me/account",
  ),
  route(
    "/me/family/children/:linkId",
    "me",
    "profile",
    "Guardians & access",
    "/me/family/approvals",
  ),
  route("/me/guardian", "me", "profile", "Your guardian", "/"),
];

const matchingRoutes = PLAYER_ROUTE_METADATA.map((metadata) => ({
  path: metadata.path,
  handle: metadata,
}));

export function resolvePlayerRoute(
  pathname: string,
): PlayerRouteMetadata | undefined {
  return matchRoutes(matchingRoutes, { pathname })?.at(-1)?.route.handle;
}
